// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

export type EnsureCurrentPlayerOptions = {
  timeoutMs?: number;
  intervalMs?: number;
  signal?: AbortSignal;
};

export type EnsureCurrentPlayerResult =
  | { status: 'resolved'; playerId: string }
  | { status: 'notFound' }
  | { status: 'timeout' }
  | { status: 'unauthorized' }
  | { status: 'error'; message: string };

const DEFAULT_TIMEOUT_MS = 10_000;
const DEFAULT_INTERVAL_MS = 400;

const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      cleanup();
      resolve();
    }, ms);

    const onAbort = () => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };

    const cleanup = () => {
      clearTimeout(timer);
      if (signal) {
        signal.removeEventListener('abort', onAbort);
      }
    };

    if (signal) {
      if (signal.aborted) {
        cleanup();
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
    }
  });

export async function ensureCurrentPlayer(
  apiBaseUrl: string,
  token: string,
  userId: string,
  options?: EnsureCurrentPlayerOptions
): Promise<EnsureCurrentPlayerResult> {
  const timeoutMs = Math.max(1, options?.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  const intervalMs = Math.max(1, options?.intervalMs ?? DEFAULT_INTERVAL_MS);
  const signal = options?.signal;
  const startedAt = Date.now();

  while (Date.now() - startedAt <= timeoutMs) {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    try {
      const response = await fetch(`${apiBaseUrl}/api/player/by-user/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });

      if (response.ok) {
        const payload = (await response.json()) as { id?: string };
        if (payload.id) {
          return { status: 'resolved', playerId: payload.id };
        }
        return { status: 'error', message: 'Missing player id in response.' };
      }

      if (response.status === 401 || response.status === 403) {
        return { status: 'unauthorized' };
      }

      if (response.status !== 404) {
        return { status: 'error', message: `Failed to load player (${response.status})` };
      }

      // Avoid hammering by-user lookups that stay unresolved for this session.
      return { status: 'notFound' };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      return { status: 'error', message: error instanceof Error ? error.message : String(error) };
    }

    if (Date.now() - startedAt > timeoutMs) {
      break;
    }

    await sleep(intervalMs, signal);
  }

  return { status: 'timeout' };
}
